/**
 * SMART on FHIR Authentication Service (Backend-Only Flow with Secure Cookies)
 *
 * SECURITY ARCHITECTURE - PRODUCTION GRADE:
 * =========================================
 * This service NEVER handles:
 * - Client credentials (client_id, client_secret)
 * - OAuth access tokens
 * - PKCE code verifiers
 * - Direct EHR communication
 * - Session tokens (now in secure HTTP-only cookies)
 *
 * Instead, all sensitive operations are delegated to the backend.
 * The frontend only knows about:
 * - Launch parameters (iss, launch) - passed from EHR
 * - Authentication status (boolean)
 *
 * SECURE COOKIE FLOW:
 * 1. Frontend receives launch from EHR (iss + launch)
 * 2. Frontend sends to backend → GET authorization URL
 * 3. Frontend redirects user to authorization URL
 * 4. EHR redirects to backend callback → Backend exchanges code for token
 * 5. Backend sets session token in secure HTTP-only cookie
 * 6. Backend redirects to frontend with auth_success flag (NO token in URL)
 * 7. Frontend makes API calls with credentials:'include' (cookies sent automatically)
 *
 * Security benefits:
 * - Session tokens in HTTP-only cookies (JavaScript cannot access)
 * - No tokens in URLs (no browser history, no logs)
 * - No tokens in localStorage/sessionStorage (XSS protection)
 * - SameSite=Strict cookies (CSRF protection)
 * - Dual validation: session token + HTTP session ID
 *
 * EPIC HYPERSPACE SUPPORT:
 * - Handles multi-instance support via ScopedStorage
 * - Preserves instance ID in URL
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { smartAuthClient, extractErrorMessage } from '../interceptor/interceptor';
import { scopedStorage, getInstanceId, setInstanceIdInUrl } from './scopedStorage';
// ==========================================
// AUTHENTICATION API
// ==========================================
/**
 * Initiate SMART launch flow.
 * Sends launch parameters to backend, receives authorization URL.
 *
 * @param launchParams - Launch parameters from EHR
 * @returns Authorization URL to redirect user to
 * @throws ApiError if launch validation fails
 */
export const initiateLaunch = (launchParams) => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield smartAuthClient.post('/launch', launchParams);
    return response.data;
});
/**
 * Get current session information.
 * SECURITY: Session token is in secure HTTP-only cookie, automatically sent.
 *
 * No need to pass session token - it's in cookies!
 *
 * @returns Session information
 * @throws ApiError if session is invalid or expired
 */
export const getSessionInfo = () => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield smartAuthClient.get('/session');
    // Fill in missing sessionToken/launchContextId from URL if available
    // The backend no longer sends these for security
    const launchContext = getInstanceId();
    if (launchContext && !response.data.sessionToken) {
        // We use the launch context (public pointer) as the session token for frontend purposes
        // (e.g. Redux state) even though the real secret is in the cookie
        response.data.sessionToken = launchContext;
    }
    return response.data;
});
/**
 * Refresh access token using refresh token.
 * SECURITY: Session token is in secure HTTP-only cookie, automatically sent.
 *
 * @returns New session information
 * @throws ApiError if refresh fails
 */
export const refreshToken = () => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield smartAuthClient.post('/token/refresh');
    // Fill in missing sessionToken/launchContextId from URL if available
    const launchContext = getInstanceId();
    if (launchContext && !response.data.sessionToken) {
        response.data.sessionToken = launchContext;
    }
    return response.data;
});
/**
 * Logout and invalidate session.
 * SECURITY: Session token is in secure HTTP-only cookie, automatically sent.
 * Backend will clear the cookie.
 */
export const logout = () => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield smartAuthClient.post('/logout');
        // Clear scoped storage for this instance
        scopedStorage.clear();
    }
    catch (_a) {
        // Don't throw - best effort logout
    }
});
// ==========================================
// SESSION MANAGEMENT (NO MORE TOKEN STORAGE!)
// ==========================================
/**
 * SECURITY: Session tokens are now in secure HTTP-only cookies.
 * Frontend JavaScript CANNOT and SHOULD NOT access them.
 *
 * All session management happens via cookies automatically.
 * These functions are kept for backward compatibility but do nothing.
 */
/**
 * @deprecated Session tokens are now in secure HTTP-only cookies.
 * This function does nothing.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const storeSessionToken = (_sessionToken) => {
    // Do nothing - session token is in HTTP-only cookie
};
/**
 * @deprecated Session tokens are now in secure HTTP-only cookies.
 * @returns null (tokens not accessible to JavaScript)
 */
export const getStoredSessionToken = () => {
    return null; // Cannot access HTTP-only cookies
};
/**
 * @deprecated Session tokens are now in secure HTTP-only cookies.
 * Backend clears the cookie on logout.
 */
export const clearSessionToken = () => {
    // Do nothing - backend clears the cookie
};
/**
 * Check if user has an active session by calling the backend.
 * @returns Promise<boolean> true if session is valid
 */
export const hasActiveSession = () => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield getSessionInfo();
        return true;
    }
    catch (_a) {
        return false;
    }
});
// ==========================================
// UTILITY FUNCTIONS
// ==========================================
/**
 * Parse error and extract user-friendly error message.
 * Uses centralized error extraction from interceptor.
 */
export const parseError = (error) => {
    return extractErrorMessage(error, 'An unexpected error occurred');
};
/**
 * Check if auth was successful from URL query parameters.
 * SECURITY: Backend no longer sends session_token in URL.
 * Instead, it sends auth_success=true flag.
 *
 * Also captures the launch_context for multi-tab support.
 */
export const isAuthSuccessInUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const authSuccess = params.get('auth_success') === 'true';
    if (authSuccess) {
        // Capture launch context if present (for multi-tab support)
        const launchContext = params.get('launch_context');
        if (launchContext) {
            // EPIC HYPERSPACE SUPPORT:
            // 1. Ensure the instance ID is preserved in the URL
            setInstanceIdInUrl(launchContext);
        }
    }
    return authSuccess;
};
/**
 * Extract error from URL query parameters.
 * Used if OAuth authorization was denied or failed.
 */
export const extractErrorFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error) {
        return {
            error,
            description: params.get('error_description') || undefined
        };
    }
    return null;
};
/**
 * Clean URL by removing query parameters.
 * Used after extracting session token or error.
 *
 * EPIC HYPERSPACE SUPPORT:
 * Preserves the 'instance' parameter so reloading the page
 * keeps the session context.
 */
export const cleanUrl = () => {
    const instanceId = getInstanceId();
    const newUrl = new URL(window.location.href);
    // Clear all query parameters
    newUrl.search = '';
    // Restore instance parameter if it exists
    if (instanceId) {
        newUrl.searchParams.set('instance', instanceId);
    }
    window.history.replaceState({}, document.title, newUrl.toString());
};
// ==========================================
// DEBUG/LOGGING (Development Only)
// ==========================================
/**
 * Log authentication flow step (development only).
 * NEVER logs sensitive data.
 */
export const logAuthStep = (_step, _data) => {
    // no-op: debug logging removed
};
